/**
 * v3.10.0 — Painel de Observações-CPF (Fase 2/3/5)
 *
 * Componente reusável que expõe:
 *   • Header collapsible com contador "Observações · N" (badge âmbar quando hoje > 0)
 *   • Ao expandir:
 *     - Textarea para nova obs (até 500 chars) + botão salvar
 *     - Sub-collapsible "Últimas 50 observações" com lista formatada
 *     - Botão "Carregar todas" (troca limit para all=true)
 *
 * Modos:
 *   • mode="standalone" (default) — clique em Salvar chama POST direto e
 *     recarrega. Usado no MoradorFormPage (Fase 3) e relatório (Fase 5).
 *   • mode="deferred" — o texto NÃO é salvo pelo painel; expõe via prop
 *     `onTextoChange(texto)` para o parent enviar junto do payload de
 *     acesso-manual (Fase 2 do RegistrarAcessoFixoModal).
 *
 * Props:
 *   cpf: string (obrigatório — sem CPF nada é carregado)
 *   nomeSnapshot: string — congelado como nome no registro da obs
 *   origem: "busca_avulsa" | "morador_form" (default varia por caller)
 *   mode: "standalone" | "deferred"
 *   onTextoChange?: (texto: string) => void — só em deferred
 *   defaultOpen?: boolean — abre o collapsible ao montar
 *   testIdPrefix?: string — para diferenciar múltiplas instâncias na tela
 *   observacaoPrincipal?: string — texto do campo fixo "observacoes" do
 *     cadastro do morador. Quando presente, é renderizado no TOPO da lista
 *     como item pinado (não conta no contador, sem edit/delete, sem data).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, ChevronRight, MessageSquare, Save, Trash2, Pencil, X, Check, Pin } from "lucide-react";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";

const MAX_TEXTO = 500;

function formatBRDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

export default function ObservacoesCpfPanel({
  cpf,
  nomeSnapshot = "",
  origem = "busca_avulsa",
  mode = "standalone",
  onTextoChange,
  defaultOpen = false,
  testIdPrefix = "obs-cpf",
  observacaoPrincipal = "",
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [counts, setCounts] = useState({ total: 0, hoje: 0 });
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Lista de observações
  const [lista, setLista] = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);
  const [listaOpen, setListaOpen] = useState(false);
  const [mostrandoTodas, setMostrandoTodas] = useState(false);

  // Textarea nova obs
  const [texto, setTexto] = useState("");
  const [salvando, setSalvando] = useState(false);

  // Edição inline
  const [editingId, setEditingId] = useState(null);
  const [editingTexto, setEditingTexto] = useState("");

  // Notifica o parent (modo deferred) sempre que o texto mudar
  const onTextoChangeRef = useRef(onTextoChange);
  onTextoChangeRef.current = onTextoChange;
  useEffect(() => {
    if (mode === "deferred" && onTextoChangeRef.current) {
      onTextoChangeRef.current(texto);
    }
  }, [texto, mode]);

  const carregarCount = useCallback(async () => {
    if (!cpf) return;
    setLoadingCounts(true);
    try {
      const r = await clienteApi.get("/cliente/observacoes-cpf/count", { params: { cpf } });
      setCounts(r.data || { total: 0, hoje: 0 });
    } catch { /* silencioso — count é bônus */ } finally {
      setLoadingCounts(false);
    }
  }, [cpf]);

  const carregarLista = useCallback(async (all = false) => {
    if (!cpf) return;
    setLoadingLista(true);
    try {
      const params = { cpf, limit: 50 };
      if (all) params.all = "true";
      const r = await clienteApi.get("/cliente/observacoes-cpf", { params });
      setLista(r.data || []);
      setMostrandoTodas(all);
    } catch (e) {
      toast.error("Erro ao carregar observações");
    } finally {
      setLoadingLista(false);
    }
  }, [cpf]);

  // Carrega count assim que CPF mudar
  useEffect(() => {
    setLista([]);
    setListaOpen(false);
    setMostrandoTodas(false);
    setTexto("");
    if (cpf) carregarCount();
  }, [cpf, carregarCount]);

  // Ao expandir a lista pela 1a vez, carrega
  useEffect(() => {
    if (listaOpen && !lista.length && !loadingLista) {
      carregarLista(false);
    }
  }, [listaOpen, lista.length, loadingLista, carregarLista]);

  const salvarObs = async () => {
    const t = (texto || "").trim();
    if (!t) { toast.error("Digite uma observação"); return; }
    if (t.length > MAX_TEXTO) { toast.error(`Máximo ${MAX_TEXTO} caracteres`); return; }
    setSalvando(true);
    try {
      await clienteApi.post("/cliente/observacoes-cpf", {
        cpf, texto: t, origem, nome_snapshot: nomeSnapshot || undefined,
      });
      toast.success("Observação registrada");
      setTexto("");
      await Promise.all([carregarCount(), listaOpen ? carregarLista(mostrandoTodas) : Promise.resolve()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar observação");
    } finally {
      setSalvando(false);
    }
  };

  const iniciarEdicao = (o) => { setEditingId(o.id); setEditingTexto(o.texto || ""); };
  const cancelarEdicao = () => { setEditingId(null); setEditingTexto(""); };
  const salvarEdicao = async (obs_id) => {
    const t = (editingTexto || "").trim();
    if (!t) { toast.error("Texto não pode ficar vazio"); return; }
    try {
      await clienteApi.patch(`/cliente/observacoes-cpf/${obs_id}`, { texto: t });
      toast.success("Observação atualizada");
      cancelarEdicao();
      await carregarLista(mostrandoTodas);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao editar observação");
    }
  };

  const removerObs = async (obs_id) => {
    if (!window.confirm("Excluir esta observação?")) return;
    try {
      await clienteApi.delete(`/cliente/observacoes-cpf/${obs_id}`);
      toast.success("Observação excluída");
      await Promise.all([carregarCount(), carregarLista(mostrandoTodas)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir observação");
    }
  };

  const contadorLabel = loadingCounts
    ? "…"
    : `${counts.total}${counts.hoje > 0 ? ` · hoje ${counts.hoje}` : ""}`;

  const principalTexto = (observacaoPrincipal || "").trim();
  const temPrincipal = principalTexto.length > 0;

  return (
    <div className="rounded-lg border border-brand-ice/15 bg-white/5" data-testid={`${testIdPrefix}-panel`}>
      {/* Header collapsible */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix}-toggle`}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors rounded-lg"
      >
        <span className="flex items-center gap-2 text-sm text-brand-ice">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <MessageSquare className="w-4 h-4 text-brand-light" />
          <span className="font-semibold">Observações</span>
        </span>
        <span
          data-testid={`${testIdPrefix}-count`}
          className={`text-[11px] font-mono px-2 py-0.5 rounded-full border ${
            counts.hoje > 0
              ? "bg-amber-500/20 text-amber-200 border-amber-400/40"
              : counts.total > 0
                ? "bg-white/10 text-brand-ice/70 border-brand-ice/20"
                : "bg-white/5 text-brand-ice/40 border-brand-ice/10"
          }`}
        >
          {contadorLabel}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-brand-ice/10" data-testid={`${testIdPrefix}-body`}>
          {/* Textarea nova obs */}
          <div className="space-y-1 pt-2">
            <label className="text-[11px] text-brand-ice/60">
              {mode === "deferred"
                ? "Nova observação (será registrada junto com este acesso)"
                : "Nova observação"}
            </label>
            <textarea
              data-testid={`${testIdPrefix}-textarea`}
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, MAX_TEXTO))}
              placeholder="Ex: entrou com betoneira azul; conferir na saída"
              rows={3}
              className="w-full bg-brand-dark/70 border border-brand-ice/15 rounded px-2 py-1.5 text-sm text-brand-ice placeholder:text-brand-ice/30 focus:outline-none focus:border-brand-light"
            />
            <div className="flex items-center justify-between text-[10px] text-brand-ice/40">
              <span>{texto.length}/{MAX_TEXTO}</span>
              {mode === "standalone" && (
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-btn-salvar`}
                  disabled={salvando || !texto.trim()}
                  onClick={salvarObs}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-brand-light/20 hover:bg-brand-light/30 disabled:opacity-40 border border-brand-light/40 text-brand-ice font-heading text-[11px]"
                >
                  <Save className="w-3 h-3" />
                  Salvar observação
                </button>
              )}
            </div>
          </div>

          {/* Sub-collapsible: Últimas 50 obs */}
          <div className="rounded border border-brand-ice/10 bg-brand-dark/40">
            <button
              type="button"
              onClick={() => setListaOpen((v) => !v)}
              data-testid={`${testIdPrefix}-lista-toggle`}
              className="w-full flex items-center justify-between px-2.5 py-1.5 text-left hover:bg-white/5 transition-colors rounded"
            >
              <span className="flex items-center gap-1.5 text-[12px] text-brand-ice/80">
                {listaOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {mostrandoTodas ? `Todas as observações (${lista.length})` : `Últimas 50 observações adicionais`}
              </span>
              {listaOpen && !mostrandoTodas && counts.total > 50 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); carregarLista(true); }}
                  data-testid={`${testIdPrefix}-btn-todas`}
                  className="text-[10px] px-2 py-0.5 rounded bg-white/5 hover:bg-white/10 border border-brand-ice/15 text-brand-ice/70"
                >
                  Carregar todas ({counts.total})
                </button>
              )}
            </button>

            {listaOpen && (
              <div className="max-h-72 overflow-auto px-2 py-1 space-y-1.5" data-testid={`${testIdPrefix}-lista-body`}>
                {/* Observação PRINCIPAL do cadastro (campo fixo cliente_moradores.observacoes)
                    fica pinada no topo, sem controles de edição, sem data. Independe da
                    coleção cliente_observacoes_cpf. */}
                {temPrincipal && (
                  <div
                    className="rounded bg-brand-light/10 border border-brand-light/40 p-2 text-xs"
                    data-testid={`${testIdPrefix}-principal`}
                  >
                    <div className="flex items-center gap-1.5 mb-1 text-[10px]">
                      <Pin className="w-3 h-3 text-brand-light" />
                      <span className="px-1.5 rounded bg-brand-light/25 text-brand-ice border border-brand-light/50 font-bold tracking-wide">
                        PRINCIPAL
                      </span>
                      <span className="text-brand-ice/50">Observação do cadastro do morador</span>
                    </div>
                    <p className="text-brand-ice whitespace-pre-wrap break-words">{principalTexto}</p>
                  </div>
                )}
                {loadingLista && <p className="text-[11px] text-brand-ice/40 italic">Carregando…</p>}
                {!loadingLista && lista.length === 0 && (
                  <p className="text-[11px] text-brand-ice/40 italic">
                    {temPrincipal
                      ? "Nenhuma observação adicional registrada para este CPF."
                      : "Nenhuma observação registrada para este CPF."}
                  </p>
                )}
                {lista.map((o) => (
                  <div key={o.id} className="rounded bg-white/5 border border-brand-ice/10 p-2 text-xs" data-testid={`${testIdPrefix}-item-${o.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="flex items-center gap-1.5 text-[10px] text-brand-ice/50">
                        <span>{formatBRDate(o.criado_em)}</span>
                        {o.evento_tipo === "entrada" && <span className="px-1.5 rounded bg-green-500/20 text-green-200 border border-green-400/30 font-bold">ENTRADA</span>}
                        {o.evento_tipo === "saida" && <span className="px-1.5 rounded bg-red-500/20 text-red-200 border border-red-400/30 font-bold">SAÍDA</span>}
                        {o.origem === "morador_form" && <span className="px-1.5 rounded bg-blue-500/20 text-blue-200 border border-blue-400/30 font-bold">CADASTRO</span>}
                        {o.origem === "busca_avulsa" && <span className="px-1.5 rounded bg-white/10 text-brand-ice/60 border border-brand-ice/20 font-bold">AVULSA</span>}
                        <span className="text-brand-ice/40">·</span>
                        <span className="text-brand-ice/60">{o.operador_nome || "—"}</span>
                        {o.editado_em && (
                          <span className="text-[9px] italic text-brand-ice/40" title={`Editada em ${formatBRDate(o.editado_em)}`}>
                            (editada)
                          </span>
                        )}
                      </span>
                      {editingId !== o.id && (
                        <span className="flex items-center gap-1">
                          <button type="button" onClick={() => iniciarEdicao(o)} title="Editar" className="p-0.5 hover:bg-white/10 rounded text-brand-ice/60 hover:text-brand-ice">
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button type="button" onClick={() => removerObs(o.id)} title="Excluir" className="p-0.5 hover:bg-red-500/20 rounded text-brand-ice/60 hover:text-red-300">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                    </div>
                    {editingId === o.id ? (
                      <div className="space-y-1">
                        <textarea
                          value={editingTexto}
                          onChange={(e) => setEditingTexto(e.target.value.slice(0, MAX_TEXTO))}
                          rows={2}
                          className="w-full bg-brand-dark/70 border border-brand-ice/15 rounded px-2 py-1 text-xs text-brand-ice"
                          data-testid={`${testIdPrefix}-edit-textarea`}
                        />
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={cancelarEdicao} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/5 hover:bg-white/10 border border-brand-ice/15 text-[10px] text-brand-ice/70">
                            <X className="w-3 h-3" /> Cancelar
                          </button>
                          <button type="button" onClick={() => salvarEdicao(o.id)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-brand-light/20 hover:bg-brand-light/30 border border-brand-light/40 text-[10px] text-brand-ice" data-testid={`${testIdPrefix}-edit-save`}>
                            <Check className="w-3 h-3" /> Salvar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-brand-ice whitespace-pre-wrap break-words">{o.texto}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
